// src/ops/OpsStateReducer.ts
//
// Ops dashboard reducer (v1)
// Consumes ops events and returns next state.
// No side effects, no I/O.
//

import type {
  OpsDashboardStateV1,
  OpsFlowWindow,
  OpsTimelineEvent,
} from "@/contracts/ops/OpsDashboardModel";
import { createEmptyOpsDashboardState } from "@/contracts/ops/OpsDashboardModel";

import type {
  SynkWiringReadyDetailV1,
  SynkOpsMetricDetailV1,
  SynkOpsViolationDetailV1,
  SynkOpsCapabilityDetailV1,
  SynkOpsActionDetailV1,
  SynkOpsTimelineDetailV1,
} from "@/contracts/ops/OpsEvents";

export type OpsReducerInput =
  | { type: "wiring_ready"; d: SynkWiringReadyDetailV1 }
  | { type: "metric"; d: SynkOpsMetricDetailV1 }
  | { type: "violation"; d: SynkOpsViolationDetailV1 }
  | { type: "capability"; d: SynkOpsCapabilityDetailV1 }
  | { type: "action"; d: SynkOpsActionDetailV1 }
  | { type: "timeline"; d: SynkOpsTimelineDetailV1 };

const clampRecent = <T>(arr: T[], max: number) =>
  arr.length <= max ? arr : arr.slice(arr.length - max);

function upsertFlowWindow(
  windows: OpsFlowWindow[],
  w: OpsFlowWindow,
  maxWindows = 3,
): OpsFlowWindow[] {
  const next = windows.filter((x) => x.windowSec !== w.windowSec);
  next.push(w);
  // keep sorted by window size
  next.sort((a, b) => a.windowSec - b.windowSec);
  return clampRecent(next, maxWindows);
}

export function reduceOpsState(
  prev: OpsDashboardStateV1 | null,
  input: OpsReducerInput,
): OpsDashboardStateV1 {
  const state = prev ?? createEmptyOpsDashboardState();
  const now = Date.now();

  // Always update "updatedAt" when we accept an input
  const base: OpsDashboardStateV1 = { ...state, updatedAt: now };

  switch (input.type) {
    case "wiring_ready": {
      const d = input.d;
      const installed = d.installed ?? [];

      const nextCaps = { ...base.capabilities };
      for (const c of installed) {
        // don't overwrite if already present except status/metadata
        nextCaps[c.id] = {
          id: c.id,
          tierRequired: c.tierRequired,
          safety: c.safety,
          status: nextCaps[c.id]?.status ?? "unknown",
          lastSeenAt: nextCaps[c.id]?.lastSeenAt ?? null,
        };
      }

      const goLiveAt = base.identity.goLiveAt ?? d.at;

      return {
        ...base,
        identity: {
          ...base.identity,
          buildId: d.buildId || "unknown",
          manifestChecksum: d.manifestChecksum || "unknown",
          env: d.env ?? "unknown",
          tier: d.tier ?? "unknown",
          authoritySource: d.authoritySource ?? { kind: "unknown", id: "unknown" },
          goLiveAt,
          mode: base.identity.mode === "unknown" ? "live" : base.identity.mode,

          // Patch: preserve/attach manifest summary (safe)
          manifestSummary: d.manifestSummary ?? base.identity.manifestSummary,
        },
        capabilities: nextCaps,
        timeline: {
          ...base.timeline,
          events: clampRecent(
            [
              ...base.timeline.events,
              {
                kind: "go_live",
                at: goLiveAt,
                detail: {
                  buildId: d.buildId,
                  tier: d.tier,
                  env: d.env,
                  nodes: d.manifestSummary?.nodes ?? 0,
                },
              },
            ],
            200,
          ),
        },
      };
    }

    case "capability": {
      const d = input.d;
      const next = {
        ...(base.capabilities[d.id] ?? {
          id: d.id,
          tierRequired: d.tierRequired,
          safety: d.safety,
          status: "unknown" as const,
          lastSeenAt: null,
        }),
        tierRequired: d.tierRequired,
        safety: d.safety,
        status: d.status,
        reason: d.reason,
        lastSeenAt:
          d.kind === "heartbeat"
            ? d.at
            : base.capabilities[d.id]?.lastSeenAt ?? d.at,
        disabledByOps: base.control.disabledCapabilities[d.id]
          ? true
          : base.capabilities[d.id]?.disabledByOps,
      };

      // timeline mapping
      const kindMap: Record<string, string> = {
        installed: "capability_installed",
        uninstalled: "capability_uninstalled",
        blocked: "capability_blocked",
        faulted: "capability_faulted",
      };

      const tlKind = kindMap[d.kind];

      return {
        ...base,
        capabilities: { ...base.capabilities, [d.id]: next },
        timeline: tlKind
          ? {
              ...base.timeline,
              events: clampRecent(
                [
                  ...base.timeline.events,
                  {
                    kind: tlKind as any,
                    at: d.at,
                    detail: {
                      id: d.id,
                      status: d.status,
                      reason: d.reason ?? "",
                    },
                  },
                ],
                200,
              ),
            }
          : base.timeline,
      };
    }

    case "metric": {
      const d = input.d;

      // Flow metric update
      if (d.kind === "flow" && d.channel) {
        const ch = base.flow.channels[d.channel] ?? { lastAt: null, windows: [] };
        const windows = d.windowSec
          ? upsertFlowWindow(ch.windows, {
              windowSec: d.windowSec,
              count: Number(d.value ?? 0),
              dropped: typeof d.dropped === "number" ? d.dropped : undefined,
            })
          : ch.windows;

        const updated = {
          ...base.flow.channels,
          [d.channel]: {
            lastAt: d.at,
            windows,
          },
        };

        // derive band cheaply: any dropped>0 on small window => warning/critical
        let band: any = base.flow.band;
        const w10 = windows.find((w) => w.windowSec === 10);
        if (w10 && (w10.dropped ?? 0) > 0) band = "warning";
        if (w10 && (w10.dropped ?? 0) > 10) band = "critical";
        if (band === "unknown") band = "normal";

        return { ...base, flow: { channels: updated, band } };
      }

      // Latency metric update
      if (d.kind === "latency") {
        const metrics = [...base.latency.metrics];
        const idx = metrics.findIndex((m) => m.name === d.name);
        const next = {
          name: d.name,
          band: d.band ?? "unknown",
          p95Ms: typeof d.p95Ms === "number" ? d.p95Ms : undefined,
          lastAt: d.at,
        };
        if (idx >= 0) metrics[idx] = next;
        else metrics.push(next);

        // derive overall latency band
        const bands = metrics.map((m) => m.band);
        const band = bands.includes("critical")
          ? "critical"
          : bands.includes("warning")
            ? "warning"
            : bands.includes("normal")
              ? "normal"
              : "unknown";

        return {
          ...base,
          latency: { ...base.latency, metrics: clampRecent(metrics, 8), band },
        };
      }

      return base;
    }

    case "violation": {
      const d = input.d;
      const recent = clampRecent(
        [
          ...base.violations.recent,
          {
            severity: d.severity,
            channel: d.channel,
            where: d.where,
            reason: d.reason,
            schema: d.schema,
            at: d.at,
          },
        ],
        50,
      );

      const counts = { ...base.violations.countsByReason };
      counts[d.reason] = (counts[d.reason] ?? 0) + 1;

      // derive band
      const band =
        d.severity === "critical"
          ? "critical"
          : d.severity === "warning" && base.violations.band !== "critical"
            ? "warning"
            : base.violations.band === "unknown"
              ? "normal"
              : base.violations.band;

      return {
        ...base,
        violations: { recent, countsByReason: counts, band },
      };
    }

    case "action": {
      const d = input.d;

      const actions = clampRecent(
        [
          ...base.ledger.actions,
          {
            at: d.at,
            actor: d.actor,
            action: d.action,
            target: d.target,
            reason: d.reason,
          },
        ],
        100,
      );

      const counts = { ...base.ledger.counts };
      counts[d.action] = (counts[d.action] ?? 0) + 1;

      // update control state
      const control = { ...base.control };
      if (d.action === "force_fallback") {
        control.fallback = { active: true, at: d.at, reason: d.reason };
      } else if (d.action === "clear_fallback") {
        control.fallback = { active: false, at: d.at };
      } else if (d.action === "disable_capability" && d.target) {
        control.disabledCapabilities = {
          ...control.disabledCapabilities,
          [d.target]: { at: d.at, actor: d.actor, reason: d.reason },
        };
      } else if (d.action === "enable_capability" && d.target) {
        const next = { ...control.disabledCapabilities };
        delete next[d.target];
        control.disabledCapabilities = next;
      }

      // also append timeline event
      const timelineEvents = clampRecent<OpsTimelineEvent>(
        [
          ...base.timeline.events,
          {
            kind: "ops_action",
            at: d.at,
            detail: {
              action: d.action,
              actor: d.actor,
              target: d.target ?? "",
              reason: d.reason ?? "",
            },
          },
        ],
        200,
      );

      return {
        ...base,
        control,
        ledger: { actions, counts },
        timeline: { ...base.timeline, events: timelineEvents },
      };
    }

    case "timeline": {
      const d = input.d;
      return {
        ...base,
        timeline: {
          ...base.timeline,
          events: clampRecent(
            [
              ...base.timeline.events,
              {
                kind: d.kind as any,
                at: d.at,
                detail: d.detail,
              },
            ],
            200,
          ),
        },
      };
    }

    default:
      return base;
  }
}
